import React, { useState } from 'react';
import { Search, Plus, MessageCircle } from 'lucide-react';
import { Conversation } from '../../hooks/useChat';
import { useSupabase } from '../../contexts/SupabaseContext';

interface ConversationListProps {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  onSelectConversation: (conversation: Conversation) => void;
  onNewConversation?: () => void;
  loading?: boolean;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedConversation,
  onSelectConversation,
  onNewConversation,
  loading = false
}) => {
  const { user } = useSupabase();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredConversations = conversations.filter(conversation => {
    // Get all participant names
    const participantNames = conversation.participants
      .filter(p => p.user)
      .map(p => `${p.user?.first_name} ${p.user?.last_name}`.toLowerCase());
    
    // Check if any participant name matches the search query
    return participantNames.some(name => name.includes(searchQuery.toLowerCase())) ||
      (conversation.topic && conversation.topic.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  const getOtherParticipantNames = (conversation: Conversation): string => {
    if (!user) return '';
    
    const otherParticipants = conversation.participants.filter(p => p.user_id !== user.id);
    
    return otherParticipants
      .filter(p => p.user)
      .map(p => `${p.user?.first_name} ${p.user?.last_name}`)
      .join(', ');
  };

  const getLastMessagePreview = (conversation: Conversation): string => {
    if (!conversation.last_message) return 'No messages yet';
    
    const content = conversation.last_message.content;
    return content.length > 30 ? `${content.substring(0, 30)}...` : content;
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    
    // If today, show time
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // If this year, show month and day
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    
    // Otherwise show date
    return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200">
        <h2 className="font-semibold text-gray-900">Messages</h2>
      </div>
      
      {/* Search and New Chat */}
      <div className="p-3 border-b border-gray-200">
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
          />
        </div>
        {onNewConversation && (
          <button
            onClick={onNewConversation}
            className="w-full flex items-center justify-center space-x-2 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition-colors text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>New Conversation</span>
          </button>
        )}
      </div>
      
      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 p-4 text-center">
            <MessageCircle className="h-8 w-8 text-gray-300 mb-2" />
            <p className="text-gray-500 text-sm">
              {searchQuery 
                ? 'No conversations match your search' 
                : 'No conversations yet'}
            </p>
          </div>
        ) : (
          <div>
            {filteredConversations.map((conversation) => (
              <button
                key={conversation.id}
                onClick={() => onSelectConversation(conversation)}
                className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                  selectedConversation?.id === conversation.id ? 'bg-orange-50' : ''
                }`}
              >
                <div className="flex justify-between items-start mb-1">
                  <div className="font-medium text-gray-900 truncate max-w-[70%]">
                    {conversation.topic || getOtherParticipantNames(conversation)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {conversation.last_message 
                      ? formatTimestamp(conversation.last_message.created_at)
                      : formatTimestamp(conversation.updated_at)}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600 truncate max-w-[85%]">
                    {getLastMessagePreview(conversation)}
                  </p>
                  {(conversation.unread_count && conversation.unread_count > 0) && (
                    <span className="bg-orange-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationList;